import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../store/gameStore';
import { LeaderboardSubmit } from './LeaderboardSubmit';
import { API_CONFIG, apiRequest } from '../config/api';

interface LeaderboardEntry {
  rank: number;
  username: string;
  coins: number;
  gems: number;
  level: number;
  prestige_level: number;
  arena_level: number;
  timestamp: string;
}

interface LeaderboardStats {
  total_players: number;
  highest_level: number;
  highest_coins: number;
  highest_gems: number;
  highest_prestige: number;
  highest_arena: number;
}

export const Leaderboard: React.FC = () => {
  const { formatNumber } = useGameStore();
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [stats, setStats] = useState<LeaderboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'level' | 'coins' | 'gems' | 'prestige_level' | 'arena_level'>('level');
  const [showStats, setShowStats] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);

  // Leaderboard abrufen
  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiRequest(`/leaderboard/top?sort=${sortBy}&limit=100`);
      const data = await response.json();
      
      if (data.success) {
        setLeaderboard(data.data);
      } else {
        setError('Fehler beim Laden des Leaderboards');
      }
    } catch (err) {
      setError('Verbindungsfehler - Backend nicht erreichbar');
      console.error('Leaderboard-Fehler:', err);
    } finally {
      setLoading(false);
    }
  };

  // Statistiken abrufen
  const fetchStats = async () => {
    try {
      const response = await apiRequest('/leaderboard/stats');
      const data = await response.json();
      
      if (data.success) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Stats-Fehler:', err);
    }
  };

  // Beim Laden und bei Sortieränderung
  useEffect(() => {
    fetchLeaderboard();
    fetchStats();
  }, [sortBy]);

  // Sortieroptionen
  const sortOptions = [
    { value: 'level', label: 'Level', icon: '⭐' },
    { value: 'coins', label: 'Münzen', icon: '💰' },
    { value: 'gems', label: 'Edelsteine', icon: '💎' },
    { value: 'prestige_level', label: 'Prestige', icon: '🌟' },
    { value: 'arena_level', label: 'Arena', icon: '⚔️' }
  ];

  // Rang-Icon basierend auf Position
  const getRankIcon = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  // Zeitstempel formatieren
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Min`;
    if (diffHours < 24) return `vor ${diffHours} Std`;
    if (diffDays < 7) return `vor ${diffDays} Tagen`;
    return date.toLocaleDateString('de-DE');
  };

  if (loading && leaderboard.length === 0) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
      >
        <div className="bg-gradient-to-br from-green-800 to-green-600 rounded-2xl p-8 text-center">
          <div className="text-4xl mb-4">📊</div>
          <div className="text-white text-xl font-bold mb-2">Leaderboard wird geladen...</div>
          <div className="text-green-200">Verbindung zum Server wird hergestellt</div>
        </div>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      >
        <motion.div 
          initial={{ scale: 0.8, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          className="bg-gradient-to-br from-green-900 to-green-700 rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-hidden"
        >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="text-center flex-1">
            <h1 className="text-3xl font-bold text-white mb-2">🏆 Leaderboard</h1>
            <p className="text-green-200">Globale Rangliste</p>
          </div>
          
          {/* Statistiken Toggle */}
          <button
            onClick={() => setShowStats(!showStats)}
            className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {showStats ? '📊 Rangliste' : '📈 Statistiken'}
          </button>
          
          {/* Senden Button */}
          <button
            onClick={() => setShowSubmit(true)}
            className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-colors ml-2"
          >
            📤 Senden
          </button>
          
          {/* Schließen Button */}
          <button
            onClick={() => window.history.back()}
            className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg transition-colors ml-4"
          >
            ✕
          </button>
        </div>

        {/* Statistiken */}
        {showStats && stats && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-800/50 rounded-xl p-6 mb-6"
          >
            <h2 className="text-2xl font-bold text-white mb-4 text-center">📈 Statistiken</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-3xl mb-2">👥</div>
                <div className="text-green-200 text-sm">Spieler</div>
                <div className="text-white font-bold text-xl">{stats.total_players}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">⭐</div>
                <div className="text-green-200 text-sm">Höchstes Level</div>
                <div className="text-white font-bold text-xl">{formatNumber(stats.highest_level)}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">💰</div>
                <div className="text-green-200 text-sm">Höchste Coins</div>
                <div className="text-white font-bold text-xl">{formatNumber(stats.highest_coins)}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">💎</div>
                <div className="text-green-200 text-sm">Höchste Gems</div>
                <div className="text-white font-bold text-xl">{formatNumber(stats.highest_gems)}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">🌟</div>
                <div className="text-green-200 text-sm">Höchstes Prestige</div>
                <div className="text-white font-bold text-xl">{stats.highest_prestige}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">⚔️</div>
                <div className="text-green-200 text-sm">Höchste Arena</div>
                <div className="text-white font-bold text-xl">{stats.highest_arena}</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Sortierung */}
        {!showStats && (
          <div className="mb-6">
            <div className="text-white font-bold mb-3">Sortierung:</div>
            <div className="flex flex-wrap gap-2">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSortBy(option.value as any)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    sortBy === option.value
                      ? 'bg-green-500 text-white'
                      : 'bg-green-700 hover:bg-green-600 text-green-200'
                  }`}
                >
                  {option.icon} {option.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Fehler */}
        {error && (
          <div className="bg-red-600 text-white p-4 rounded-lg mb-6 text-center">
            <div className="text-xl mb-2">⚠️</div>
            <div className="font-bold">{error}</div>
            <button
              onClick={fetchLeaderboard}
              className="bg-red-500 hover:bg-red-400 px-4 py-2 rounded-lg mt-3 transition-colors"
            >
              Erneut versuchen
            </button>
          </div>
        )}

        {/* Leaderboard Tabelle */}
        {!showStats && (
          <div className="overflow-y-auto max-h-[60vh]">
            {leaderboard.length === 0 && !loading ? (
              <div className="text-center text-green-200 py-8">
                <div className="text-4xl mb-4">📭</div>
                <div className="text-xl font-bold">Noch keine Einträge</div>
                <div className="text-sm">Sei der Erste im Leaderboard!</div>
              </div>
            ) : (
              <div className="bg-green-800/30 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-green-800/50">
                    <tr>
                      <th className="text-left p-4 text-white font-bold">Rang</th>
                      <th className="text-left p-4 text-white font-bold">Spieler</th>
                      <th className="text-right p-4 text-white font-bold">Level</th>
                      <th className="text-right p-4 text-white font-bold">Münzen</th>
                      <th className="text-right p-4 text-white font-bold">Gems</th>
                      <th className="text-right p-4 text-white font-bold">Prestige</th>
                      <th className="text-right p-4 text-white font-bold">Arena</th>
                      <th className="text-right p-4 text-white font-bold">Aktualisiert</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.map((entry, index) => (
                      <motion.tr
                        key={entry.username}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`border-b border-green-700/30 hover:bg-green-700/20 transition-colors ${
                          index < 3 ? 'bg-yellow-500/10' : ''
                        }`}
                      >
                        <td className="p-4">
                          <div className="text-2xl font-bold text-yellow-400">
                            {getRankIcon(entry.rank)}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="text-white font-bold">{entry.username}</div>
                        </td>
                        <td className="p-4 text-right">
                          <div className="text-white font-bold">{formatNumber(entry.level)}</div>
                        </td>
                        <td className="p-4 text-right">
                          <div className="text-green-300">{formatNumber(entry.coins)}</div>
                        </td>
                        <td className="p-4 text-right">
                          <div className="text-blue-300">{formatNumber(entry.gems)}</div>
                        </td>
                        <td className="p-4 text-right">
                          <div className="text-purple-300">{entry.prestige_level}</div>
                        </td>
                        <td className="p-4 text-right">
                          <div className="text-red-300">{entry.arena_level}</div>
                        </td>
                        <td className="p-4 text-right">
                          <div className="text-green-200 text-sm">
                            {formatTimestamp(entry.timestamp)}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

              {/* Footer */}
              <div className="mt-6 text-center text-green-200 text-sm">
                <div className="mb-2">💡 Tipp: Klicke auf die Sortierung um verschiedene Kategorien zu vergleichen</div>
                <div>Das Leaderboard wird automatisch aktualisiert</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Submit Modal */}
          {showSubmit && (
            <LeaderboardSubmit onClose={() => setShowSubmit(false)} />
          )}
        </>
      );
    }; 